import * as RadixTooltip from "@radix-ui/react-tooltip";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <RadixTooltip.Provider delayDuration={200} skipDelayDuration={300}>
      {children}
    </RadixTooltip.Provider>
  );
}

/**
 * A tooltip that is decoration, never the only carrier of meaning: every trigger
 * that uses one also has an `aria-label` or visible text, because a tooltip is
 * unreachable on touch and easy to miss on a dense table.
 */
export function Tooltip({
  content,
  children,
  side = "top",
  className,
}: {
  content: ReactNode;
  children: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
}) {
  if (content === null || content === undefined || content === "") return <>{children}</>;
  return (
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          sideOffset={6}
          className={cn(
            "z-50 max-w-xs rounded-md border border-[var(--color-border)] bg-[var(--color-elevated)]",
            "px-2.5 py-1.5 text-[11px] leading-relaxed text-[var(--color-fg-muted)] shadow-lg",
            "animate-in fade-in-0 zoom-in-95",
            className,
          )}
        >
          {content}
          <RadixTooltip.Arrow className="fill-[var(--color-elevated)]" />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}
